import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import { User, Share2, Wallet, TrendingUp, Clock, ArrowUpRight, ArrowDownLeft, Trophy, Gift, CheckCircle, XCircle, Shield, Ticket, Star, ChevronRight } from 'lucide-react';
import { TransactionType, TransactionStatus, GamificationProfile } from '@/types';
import { api } from '@/services/api';
import { toast } from '@/store/useToast';

interface RaffleHistoryItem {
  entry_id: number;
  raffle_id: number;
  raffle_title: string;
  raffle_status: string;
  raffle_photo_url?: string;
  amount_paid: number;
  won: boolean;
  prize_name?: string;
  created_at: string;
}

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, referralStats, transactions, fetchReferralStats, fetchTransactions, createTestDeposit } = useStore();
  const [depositAmount, setDepositAmount] = useState('1000');
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [raffleHistory, setRaffleHistory] = useState<RaffleHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [gamification, setGamification] = useState<GamificationProfile | null>(null);

  useEffect(() => {
    fetchReferralStats();
    fetchTransactions();
    loadRaffleHistory();
    loadGamification();
  }, []);

  const loadGamification = async () => {
    try {
      const data = await api.getGamification();
      setGamification(data);
    } catch {
      // silent
    }
  };

  const loadRaffleHistory = async () => {
    try {
      setLoadingHistory(true);
      const data = await api.getRaffleHistory();
      setRaffleHistory(data);
    } catch {
      // silent
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleTestDeposit = async () => {
    try {
      await createTestDeposit(Number(depositAmount));
      toast.success('Баланс успешно пополнен!');
    } catch (error) {
      toast.error('Ошибка пополнения');
    }
  };

  const copyReferralLink = () => {
    const botUsername = import.meta.env.VITE_BOT_USERNAME;
    const link = `https://t.me/${botUsername}?start=${user?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.info('Ссылка скопирована!');
  };

  const getTransactionIcon = (type: TransactionType) => {
    switch (type) {
      case TransactionType.DEPOSIT:
        return <ArrowDownLeft className="w-5 h-5 text-green-500" />;
      case TransactionType.RAFFLE_ENTRY:
        return <ArrowUpRight className="w-5 h-5 text-red-500" />;
      case TransactionType.RAFFLE_WIN:
        return <Trophy className="w-5 h-5 text-yellow-500" />;
      case TransactionType.REFERRAL_BONUS:
        return <Gift className="w-5 h-5 text-purple-500" />;
      case TransactionType.JACKPOT_BET:
        return <ArrowUpRight className="w-5 h-5 text-orange-500" />;
      case TransactionType.JACKPOT_WIN:
        return <Trophy className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTransactionTypeLabel = (type: TransactionType) => {
    switch (type) {
      case TransactionType.DEPOSIT:
        return 'Пополнение';
      case TransactionType.RAFFLE_ENTRY:
        return 'Вход в розыгрыш';
      case TransactionType.RAFFLE_WIN:
        return 'Выигрыш в розыгрыше';
      case TransactionType.REFERRAL_BONUS:
        return 'Реферальный бонус';
      case TransactionType.JACKPOT_BET:
        return 'Ставка в джекпоте';
      case TransactionType.JACKPOT_WIN:
        return 'Выигрыш в джекпоте';
      case TransactionType.REFUND:
        return 'Возврат средств';
      default:
        return 'Неизвестно';
    }
  };

  const getAmountColor = (type: TransactionType) => {
    switch (type) {
      case TransactionType.DEPOSIT:
      case TransactionType.RAFFLE_WIN:
      case TransactionType.REFERRAL_BONUS:
      case TransactionType.JACKPOT_WIN:
      case TransactionType.REFUND:
        return 'text-green-600 dark:text-green-400';
      case TransactionType.RAFFLE_ENTRY:
      case TransactionType.JACKPOT_BET:
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    if (diffDays < 7) return `${diffDays} дн назад`;

    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const displayedTransactions = showAllTransactions ? transactions : transactions.slice(0, 5);

  return (
    <div className="pb-20 max-w-7xl mx-auto px-4 py-6">
      {/* User Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-telegram-button rounded-full flex items-center justify-center">
            <User className="text-telegram-buttonText" size={32} />
          </div>
          <div>
            <h2 className="text-xl font-bold">{user?.first_name}</h2>
            <p className="text-gray-500">@{user?.username}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <Wallet className="text-green-500 mb-2" size={20} />
            <div className="text-sm text-gray-500">Баланс</div>
            <div className="text-xl font-bold">{user?.balance.toFixed(2)} ₽</div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <TrendingUp className="text-blue-500 mb-2" size={20} />
            <div className="text-sm text-gray-500">Заработано</div>
            <div className="text-xl font-bold">{user?.referral_earnings.toFixed(2)} ₽</div>
          </div>
        </div>
      </div>

      {/* Gamification */}
      {gamification && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-md mb-6">
          {/* Level + XP bar header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Star className="text-yellow-500" size={20} />
              <span className="font-bold text-base">
                Уровень {gamification.level} · {gamification.level_name}
              </span>
            </div>
            <button
              onClick={() => navigate('/leaderboard')}
              className="flex items-center gap-1 text-sm text-telegram-button font-medium"
            >
              Рейтинг
              <ChevronRight size={16} />
            </button>
          </div>
          {/* XP progress bar */}
          <div className="mb-1 flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{gamification.xp} XP</span>
            <span>{gamification.next_level_xp} XP</span>
          </div>
          <div className="w-full h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-4">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all"
              style={{ width: `${Math.min(gamification.xp_progress_pct, 100)}%` }}
            />
          </div>
          {/* Achievements grid */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Достижения
            </span>
            <span className="text-xs text-gray-400">
              {gamification.achievements.filter(a => a.unlocked).length}/{gamification.achievements.length}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {gamification.achievements.map((ach) => (
              <div
                key={ach.key}
                className={`flex flex-col items-center p-2 rounded-lg border text-center transition-colors ${
                  ach.unlocked
                    ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700'
                    : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 opacity-50'
                }`}
              >
                <span className="text-2xl mb-1">{ach.icon}</span>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 leading-tight">
                  {ach.name}
                </span>
                <span className="text-xs text-purple-500 dark:text-purple-400 mt-0.5">
                  +{ach.xp_reward} XP
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin Panel Button */}
      {user?.is_admin && (
        <div className="bg-gradient-to-r from-red-500 to-purple-600 rounded-lg p-6 shadow-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="text-white" size={32} />
              <div>
                <h3 className="text-xl font-bold text-white">Админ-панель</h3>
                <p className="text-white/80 text-sm">Управление розыгрышами</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="bg-white text-purple-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors"
            >
              Открыть
            </button>
          </div>
        </div>
      )}

      {/* Deposit Section */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 shadow-md mb-6">
        <h3 className="text-lg font-bold mb-3">💳 Пополнение баланса</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Интеграция с платежными системами в разработке.
        </p>
        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            🚧 Функция скоро появится
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            Для пополнения баланса обратитесь к администратору
          </p>
        </div>
      </div>

      {/* Referral */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
        <h3 className="text-lg font-bold mb-3">Реферальная программа</h3>

        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-4">
          <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
            Ваш реферальный код
          </div>
          <div className="flex items-center justify-between">
            <code className="text-lg font-bold">{user?.referral_code}</code>
            <button
              onClick={copyReferralLink}
              className="px-4 py-2 bg-telegram-button text-telegram-buttonText rounded-lg flex items-center gap-2"
            >
              <Share2 size={16} />
              Поделиться
            </button>
          </div>
        </div>

        {referralStats && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-500">Всего рефералов</div>
              <div className="text-2xl font-bold">{referralStats.total_referrals}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Заработано</div>
              <div className="text-2xl font-bold">{referralStats.total_earnings} ₽</div>
            </div>
          </div>
        )}

        <p className="text-sm text-gray-500 mt-4">
          💰 Получайте 10% от каждого пополнения ваших рефералов!
        </p>
      </div>

      {/* Transaction History */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md mt-6">
        <h3 className="text-lg font-bold mb-4">История транзакций</h3>

        {transactions.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Пока нет транзакций
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {displayedTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-100 dark:border-gray-600"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="mt-1">
                        {getTransactionIcon(transaction.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm">
                          {getTransactionTypeLabel(transaction.type)}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(transaction.created_at)}
                        </p>
                        {transaction.description && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {transaction.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <p className={`font-bold ${getAmountColor(transaction.type)}`}>
                        {[TransactionType.RAFFLE_ENTRY, TransactionType.JACKPOT_BET].includes(transaction.type) ? '-' : '+'}
                        {transaction.amount.toFixed(2)} ₽
                      </p>
                      {transaction.status === TransactionStatus.PENDING && (
                        <span className="text-xs text-yellow-500 flex items-center justify-end gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          В обработке
                        </span>
                      )}
                      {transaction.status === TransactionStatus.FAILED && (
                        <span className="text-xs text-red-500 flex items-center justify-end gap-1 mt-1">
                          <XCircle className="w-3 h-3" />
                          Ошибка
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {transactions.length > 5 && (
              <button
                onClick={() => setShowAllTransactions(!showAllTransactions)}
                className="w-full mt-4 py-2 text-sm text-telegram-button hover:text-telegram-button/80 font-medium"
              >
                {showAllTransactions ? 'Скрыть' : `Показать все (${transactions.length})`}
              </button>
            )}
          </>
        )}
      </div>

      {/* Raffle History */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md mt-6">
        <h3 className="text-lg font-bold mb-4">История розыгрышей</h3>

        {loadingHistory ? (
          <div className="text-center py-8 text-gray-500 text-sm">Загрузка...</div>
        ) : raffleHistory.length === 0 ? (
          <div className="text-center py-8">
            <Ticket className="w-12 h-12 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">Вы ещё не участвовали в розыгрышах</p>
          </div>
        ) : (
          <div className="space-y-3">
            {raffleHistory.map((item) => (
              <div
                key={item.entry_id}
                onClick={() => navigate(`/raffle/${item.raffle_id}`)}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-100 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm truncate">{item.raffle_title}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {formatDate(item.created_at)} · {item.amount_paid.toFixed(0)}₽
                    </p>
                    {item.won && item.prize_name && (
                      <p className="text-xs text-yellow-600 dark:text-yellow-400 font-medium mt-0.5">
                        🏆 {item.prize_name}
                      </p>
                    )}
                  </div>
                  <div className="ml-3 flex-shrink-0">
                    {item.raffle_status === 'completed' ? (
                      item.won ? (
                        <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400 rounded-full font-medium">
                          Победа
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 dark:bg-gray-600 rounded-full">
                          Не выиграл
                        </span>
                      )
                    ) : item.raffle_status === 'active' ? (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 rounded-full font-medium">
                        Идёт
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 dark:bg-gray-600 rounded-full">
                        {item.raffle_status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
